const SHORT_HEX = /^[0-9a-f]{3}$/i;
const FULL_HEX = /^[0-9a-f]{6}$/i;
// Step size of the WOW lighten/darken helpers this ports (water-on-web colors.ts).
const LIGHTEN_INTERVAL = 40;

type Rgb = [number, number, number];

export function isHexColor(color: string): boolean {
  const cleanHex = color.replace('#', '');
  return SHORT_HEX.test(cleanHex) || FULL_HEX.test(cleanHex);
}

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const stripped = value.trim().replace(/^(0x|#)/i, '');
  if (SHORT_HEX.test(stripped)) {
    const expanded = stripped
      .split('')
      .map((digit) => digit + digit)
      .join('');
    return `#${expanded.toLowerCase()}`;
  }
  if (FULL_HEX.test(stripped)) return `#${stripped.toLowerCase()}`;
  return null;
}

const hexToRgb = (hex: string): Rgb => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) throw new Error(`Invalid hex color: ${hex}`);
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  ];
};

const clampChannel = (channel: number): number =>
  Math.min(255, Math.max(0, Math.round(channel)));

const rgbToHex = (rgb: Rgb): string =>
  `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;

export function lightenHex(hex: string, steps: number): string {
  const delta = LIGHTEN_INTERVAL * steps;
  const rgb = hexToRgb(hex).map((channel) => clampChannel(channel + delta));
  return rgbToHex(rgb as Rgb);
}

export function darkenHex(hex: string, steps: number): string {
  return lightenHex(hex, -steps);
}

export function isCloseToWhite(hex: string): boolean {
  const [r, g, b] = hexToRgb(hex);
  return (r * 0.299 + g * 0.587 + b * 0.114) / 256 > 0.9;
}
